import { inject, Injectable, computed, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { LoginRequest, LoginResponse, UserRole } from './auth.models';

const AUTH_STORAGE_KEY = 'eaea_auth_token';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiService = inject(ApiService);
  private readonly router = inject(Router);

  private readonly tokenSignal = signal<string | null>(this.loadToken());
  isAuthenticated = computed(() => !!this.tokenSignal());
  readonly userRole = computed<UserRole | null>(() => {
    const payload = this.jwtPayload();
    const rawRole = payload?.['role'] ?? payload?.['roles'];

    if (typeof rawRole === 'string') {
      return rawRole === 'ADMIN' ? 'ADMIN' : rawRole === 'STUDENT' ? 'STUDENT' : null;
    }

    if (Array.isArray(rawRole) && rawRole.length > 0 && typeof rawRole[0] === 'string') {
      return rawRole[0] === 'ADMIN' ? 'ADMIN' : rawRole[0] === 'STUDENT' ? 'STUDENT' : null;
    }

    return null;
  });
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);

  get token() {
    return this.tokenSignal();
  }

  async login(payload: LoginRequest): Promise<LoginResponse> {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      const response = await firstValueFrom(this.apiService.post<LoginResponse>('auth/login', payload));
      this.setToken(response.token);
      return response;
    } catch (error: unknown) {
      this.errorMessage.set(this.resolveErrorMessage(error));
      return Promise.reject(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  async register(payload: LoginRequest): Promise<LoginResponse> {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    try {
      const response = await firstValueFrom(this.apiService.post<LoginResponse>('auth/register', payload));
      this.setToken(response.token);
      return response;
    } catch (error: unknown) {
      this.errorMessage.set(this.resolveErrorMessage(error));
      return Promise.reject(error);
    } finally {
      this.isLoading.set(false);
    }
  }

  logout(returnUrl?: string | null): void {
    this.clearToken();
    void this.router.navigate(['/auth'], {
      queryParams: returnUrl ? { returnUrl } : undefined,
    });
  }

  private setToken(token: string): void {
    this.tokenSignal.set(token);
    localStorage.setItem(AUTH_STORAGE_KEY, token);
  }

  private clearToken(): void {
    this.tokenSignal.set(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }

  private loadToken(): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    return localStorage.getItem(AUTH_STORAGE_KEY);
  }

  private jwtPayload(): Record<string, unknown> | null {
    const token = this.tokenSignal();
    if (!token) {
      return null;
    }

    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    try {
      const rawPayload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
      const json = decodeURIComponent(
        rawPayload
          .split('')
          .map((char) => '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );

      return JSON.parse(json) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  hasAnyRole(requiredRoles: ReadonlyArray<UserRole>): boolean {
    const role = this.userRole();
    return role !== null && requiredRoles.includes(role);
  }

  defaultRoute(): string {
    const role = this.userRole();
    return role === 'ADMIN' ? '/admin' : role === 'STUDENT' ? '/student' : '/';
  }

  private resolveErrorMessage(error: unknown): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    return 'Error de comunicación con el servidor.';
  }
}
